/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * ORTB body validation (#858) — warns when an outbound BidRequest is missing
 * important OpenRTB fields, **aware of what each mode actually populates**:
 *
 * - **replace** — the template IS the whole request, so a required/recommended
 *   catalog field that isn't in the template simply won't be sent.
 * - **merge** — the driver builds `id`/`imp`/`device` identity/`site|app` and
 *   the auction scalars from the session (the "merge base",
 *   {@link ORTB_DRIVER_MERGE_BASE}), then overlays the template. So a field is
 *   only flagged when **neither** the merge base **nor** the template provides
 *   it — e.g. privacy signals (`regs`) and supply chain (`source`), which the
 *   driver can't derive.
 *
 * Structural problems (both modes): `site` AND `app` together; in replace mode,
 * no `imp` at all, or no inventory context.
 */

import type { FindingKind } from '@finding-kind';
import { expressionStructureIssues, rootCalculatedPath } from './expression-structure';
import {
  checkYieldAgainstDestination,
  coercibleScalar,
  convertScalar,
  isExpressionYieldType,
  rowYield,
  runtimeCoercion,
} from '@expression-yield';
import type { ExpressionLocation } from './expression-walk';
import type { ExpressionNameLookup } from './expression-summary';
import {
  appliesTo,
  checkDestinationReference,
  checkDestinationValue,
  encodingAfterCoercion,
  DESTINATION_TYPE_PHRASE,
  isDeferredText,
  isRequiredHere,
  type DestinationField,
} from '@destination-field';
import {
  isOrtbBodyLeafNode,
  ortbBodyLeafValueType,
  ORTB_BODY_PATTERN_AT,
  ORTB_BODY_PATTERN_EACH,
  type OrtbBodyLeafNode,
} from '@ortb-body-leaf';
import { authoredFloorLiteral, describeFloorBelowBound, type FloorBound } from '../../../utils/floor-bound';
import {
  bodyLeafRendering,
  bodyToLeaves,
  catalogFieldForPath,
  pathToLabel,
  type PathSegment,
} from './ortb-body-editor.adapters';
import { describeArtifactMetadataOnWire, describeRequiredRisk, leafDefault } from './mapping-value-round-trip';
import {
  ORTB_FIELD_CATALOG,
  driverMergeBaseCovers,
  fieldSpellings,
  fieldsForVersion,
  getMissingStandardFields,
  nameForVersion,
  normalizeOrtbVersion,
  ortbFieldSentType,
} from '@ortb-field-catalog';
import { mergeBaseFieldAt } from '@ortb-merge-base';
import type { OrtbBodyTemplate } from '../../../types/demand-source';

/** A field an issue can be auto-fixed by adding: section + section-relative path. */
export interface OrtbFieldRef {
  section: string;
  relPath: string;
  /**
   * **A move, not an add** — the section-relative location whose leaves this fix relocates to
   * `relPath` (`#1315`'s version pairs: `regs.ext.gdpr` → `regs.gdpr` on 2.6, and back on 2.5).
   *
   * Absent is the add every other fix is. A version-location finding cannot be fixed by adding, because
   * the field is already authored — adding the other location beside it would put the signal on the
   * wire twice, which is why that finding had no Fix at all. Moving keeps the operator's value, its
   * mapping and its provenance, and changes only where it is sent.
   */
  movedFrom?: string;
}

export interface OrtbBodyIssue {
  /** What kind of thing is wrong; the level follows it (`3i`, `D41`) — see `MappingIssue.kind`. */
  kind: FindingKind;
  message: string;
  /** Dot path the issue relates to, when applicable. */
  path?: string;
  /**
   * Fields that resolve this issue when added (via the editor's default-mapped
   * add path). Present on "won't be populated" coverage gaps and the
   * missing-imp / missing-inventory structural issues; absent on conflicts
   * (which are fixed by removal, not addition).
   */
  fix?: OrtbFieldRef[];
  /** Where inside the leaf's expression (`4c`, `#1334`) — see `MappingIssue.at`. */
  at?: ExpressionLocation;
}

/** The required + recommended fields of a section, as fix refs. */
function standardFixFor(section: string, protocolVersion: string | null): OrtbFieldRef[] {
  // Only the replace-mode structural issues use this, where nothing is built beneath the body.
  return getMissingStandardFields(section, new Set(), protocolVersion, 'replace').map((f) => ({
    section,
    relPath: f.name,
  }));
}

/**
 * The value at a body path, or `undefined`.
 *
 * `has` answers *is anything there*; the value sweep needs **what** is there, and inlining a
 * second walker beside this one is how the two come to disagree about `imp[0]`.
 */
function read(body: Record<string, unknown>, path: string[]): unknown {
  let cur: any = body;
  for (const seg of path) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = Array.isArray(cur) ? cur[Number(seg)] : cur[seg];
  }
  return cur;
}

function has(body: Record<string, unknown>, path: string[]): boolean {
  let cur: any = body;
  for (const seg of path) {
    if (cur == null || typeof cur !== 'object') return false;
    cur = Array.isArray(cur) ? cur[Number(seg)] : cur[seg];
  }
  return cur !== undefined && cur !== null;
}

/**
 * Body path for a section-relative field — `imp` fields target `imp[0]`, and `request` is the
 * document root.
 *
 * **`request` is a grouping, not a path segment** (`#1194`). `ORTB_FIELD_CATALOG` files the nine
 * top-level `BidRequest` scalars — `at`, `tmax`, `cur`, `test`, `bcat`, `badv`, `bapp`, `wseat`,
 * `wlang` — under a section named `request` so they render together, and OpenRTB has no member of
 * that name: `at` lives at the root. This validator judges a body at the place the **protocol**
 * puts it, which is also where `templateToLeaves` reads it from.
 *
 * Where the editor *writes* it is a different question and today a wrong one — `buildFieldPath`
 * treats the section as a segment, so a field added from the catalog lands under a `request`
 * wrapper that reaches the wire. That is `#1194`, deliberately not fixed here.
 *
 * It has never mattered before because every field in the section is `importance: 'optional'`, and
 * the coverage sweep skips those — so this line was unreachable for `request` until the value sweep
 * arrived.
 */
/**
 * **Where one impression's fields are read from** (RS4c): `imp.$each` — what every impression the
 * driver builds receives — or, in a replace body that holds a literal `imp` array, its first
 * element. The display is the row label for the same place (`pathToLabel`), so a finding and the
 * row it is about name it one way: `imp[*].video.w`.
 */
interface ImpScope {
  segments: string[];
  display: string;
}

function everyImpressionScope(body: unknown): ImpScope {
  return Array.isArray((body as { imp?: unknown })?.imp)
    ? { segments: ['imp', '0'], display: 'imp[0]' }
    : { segments: ['imp', ORTB_BODY_PATTERN_EACH], display: 'imp[*]' };
}

/**
 * **Every place an impression's value is authored** — `$each` and each `$at` key, or every element
 * of a literal array. The value sweep grades each, because an override is a value exactly as much as
 * the default beneath it is.
 */
function impScopes(body: unknown): ImpScope[] {
  const imp = (body as { imp?: unknown })?.imp;
  if (Array.isArray(imp)) return imp.map((_, i) => ({ segments: ['imp', String(i)], display: `imp[${i}]` }));
  const scopes: ImpScope[] = [{ segments: ['imp', ORTB_BODY_PATTERN_EACH], display: 'imp[*]' }];
  const at = imp && typeof imp === 'object' ? (imp as { $at?: unknown }).$at : undefined;
  if (at && typeof at === 'object') {
    for (const key of Object.keys(at)) {
      scopes.push({ segments: ['imp', ORTB_BODY_PATTERN_AT, key], display: `imp[${key}]` });
    }
  }
  return scopes;
}

function fieldSegments(section: string, relPath: string, imp?: ImpScope): string[] {
  const rel = relPath.split('.');
  if (section === 'imp') return [...(imp ?? everyImpressionScope(undefined)).segments, ...rel];
  if (section === 'request') return rel;
  return [section, ...rel];
}

/** Human-readable dot/bracket path for a section-relative field — the same shape `fieldSegments` walks. */
function displayPath(section: string, relPath: string, imp?: ImpScope): string {
  if (section === 'imp') return `${(imp ?? everyImpressionScope(undefined)).display}.${relPath}`;
  // `request` groups the top-level scalars and is not part of the path (`#1194`). Naming a
  // section the body has no member of would point the operator at a field that is not there.
  if (section === 'request') return relPath;
  return `${section}.${relPath}`;
}

export function validateOrtbBody(
  template: OrtbBodyTemplate | undefined | null,
  /**
   * **Names what a leaf addresses, so the required-risk warning can see an inherited default**
   * (`X36`, `XS5`). Without it a leaf on a shared expression that declares a fallback reads as
   * *required and derived with no default* — a surface warning about correct behaviour, which is
   * the exact defect `#1269` fixed for a calculation and which a reference reintroduces one layer
   * up. `null` means the inherited layer is unknown here, and the row's **own** override is still
   * read.
   *
   * **Required and nullable rather than optional** (`#1134`, applied to an argument rather than a
   * prop, `#1254`). It was `expressionLookup?:` and the audit that produced says why: of four
   * readers, **one** passed it. The detail page's `collectSourceIssues` did not, and neither did
   * either of the wizard's two — so three of four warned *required and derived with no default* on
   * a leaf whose shared expression declares one, which is the defect this parameter exists to
   * prevent, reintroduced by every reader except the one that added it.
   *
   * An optional argument through a shared function is the same shape as an optional prop through a
   * shared component: it compiles, it type-checks, and it silently answers with less. A surface
   * that genuinely cannot look an expression up passes `null` and says so.
   */
  expressionLookup: ExpressionNameLookup | null,
  /**
   * **The source's OpenRTB version, so the coverage sweep reads each field's `when`** (#1315). The
   * seven privacy signals 2.6 moved out of `ext` are declared as a pair, one row per version, and a
   * sweep that ignores the version demands both. `null` means the authored default (2.6).
   *
   * Required and nullable for the reason `expressionLookup` is: optional, a caller that forgot
   * would silently validate against the default version.
   */
  protocolVersion: string | null,
  /**
   * **What the source's terms do to an authored floor** (`#918`, Todd 2026-09-18), so an
   * `imp.bidfloor` the driver will raise is warned about where it is authored. Required and nullable
   * for the same reason as the two above; `null` means the source's terms are not known here.
   */
  floor: FloorBound | null,
): OrtbBodyIssue[] {
  const body = template?.body ?? {};
  const every = everyImpressionScope(body);
  const mode = template?.mode ?? 'merge';
  const issues: OrtbBodyIssue[] = [];

  const hasSite = has(body, ['site']);
  const hasApp = has(body, ['app']);
  const impPresent = has(body, ['imp']) && (!Array.isArray((body as any).imp) || (body as any).imp.length > 0);

  // ── Structural ───────────────────────────────────────────────────────────
  // site + app together — flagged in both modes.
  if (hasSite && hasApp) {
    issues.push({
      kind: 'conflict',
      message: 'Both `site` and `app` are set — OpenRTB treats these as mutually exclusive; most exchanges expect one.',
      path: 'app',
    });
  }

  if (mode === 'replace') {
    if (!impPresent) {
      issues.push({
        kind: 'incomplete',
        message: 'No `imp` — a replace body must carry at least one impression.',
        path: 'imp',
        fix: standardFixFor('imp', protocolVersion),
      });
    }
    if (!hasSite && !hasApp) {
      issues.push({
        kind: 'missing-value',
        message: 'No `site` or `app` — add an inventory context so the exchange knows where the ad runs.',
        path: 'app',
        fix: standardFixFor('app', protocolVersion),
      });
    }
  }

  // ── Coverage sweep — required/recommended fields that won't be populated ──
  // The inventory context is site-or-app: only validate the one actually used
  // (in merge mode the driver supplies one, so skip both unless the template
  // explicitly adds fields to it).
  const inventoryUsed: 'app' | 'site' | null = hasApp ? 'app' : hasSite ? 'site' : null;

  for (const section of Object.keys(ORTB_FIELD_CATALOG)) {
    // Skip the inventory context the body isn't using.
    if ((section === 'site' || section === 'app') && section !== inventoryUsed) continue;
    // If `imp` is entirely absent in replace mode, the structural error covers it —
    // don't also list every missing imp field.
    if (section === 'imp' && !impPresent) continue;

    // **Through `fieldsForVersion`, never the raw catalog** (#1315): a row whose `when` excludes this
    // version is de-weighted to optional, so a 2.6 source is asked for `regs.gdpr` and a 2.5 source
    // for `regs.ext.gdpr` — never both.
    for (const f of fieldsForVersion(section, protocolVersion ?? undefined)) {
      if (f.importance === 'optional') continue;
      // Present under **any** spelling of the field: a body carrying `regs.ext.gdpr` has a GDPR flag
      // whatever version the source declares, and `video.plcmt` is a placement (#1112).
      if (fieldSpellings(f.name).some((name) => has(body, fieldSegments(section, name, every)))) continue;
      if (mode === 'merge' && driverMergeBaseCovers(section, f.name)) continue; // driver supplies it

      const display = displayPath(section, f.name, every);
      const claim =
        f.importance === 'required' ? `\`${display}\` is required by most exchanges` : `\`${display}\` is recommended`;
      // **Said from the declaration** (`#1318` item 3). A privacy signal is `forwarded-if-sent`: the
      // driver sends the publisher's value and never invents one, so *"the driver won't add it"* was
      // false about every request that carried one. It is still a warning, because a request with no
      // consent signal is one an exchange may refuse — the certainty's own docblock says so.
      const forwarded = mode === 'merge' && mergeBaseFieldAt(`${section}.${f.name}`)?.certainty === 'forwarded-if-sent';
      issues.push({
        kind: 'missing-value',
        message: forwarded
          ? `${claim} and goes out only when the publisher sends one — the driver forwards it and never fills it in.`
          : mode === 'merge'
            ? `${claim} but isn't set — the driver won't add it.`
            : `${claim} but isn't set.`,
        path: display,
        fix: [{ section, relPath: f.name }],
      });
    }
  }

  /**
   * ── Version sweep — an authored field this source's version does not read ──
   *
   * Keyed on `when`, not on `supersedes` (#1315): *does this row apply to this receiver* is the
   * question, and `appliesTo` is its one evaluator. `supersedes` only supplies the name to point at,
   * so a field added in a later version with no older spelling is still caught — it simply has no
   * name to offer.
   *
   * A warning, never a block: the driver sends the body as written, so the value does go out, and an
   * exchange may read the older location anyway.
   *
   * **Its Fix moves the field** (Todd, 2026-09-28: *"We should be able to fix it right?"*). Adding the
   * version's location would put the signal on the wire twice, which is why this finding had none;
   * a move keeps what was authored and changes where it goes. Only where the version's location is
   * empty — when both are set, which value is meant is the operator's call — and never on `imp`,
   * whose fields are scoped per impression and have no version pairs today.
   */
  const version = normalizeOrtbVersion(protocolVersion ?? undefined);
  for (const [section, fields] of Object.entries(ORTB_FIELD_CATALOG)) {
    for (const f of fields) {
      if (!f.when || appliesTo(f, { protocolVersion: version })) continue;
      if (!has(body, fieldSegments(section, f.name, every))) continue;

      const display = displayPath(section, f.name, every);
      const current = nameForVersion(section, f.name, version);
      const currentDisplay = current ? displayPath(section, current, every) : undefined;
      let message: string;
      let movable = false;
      if (!currentDisplay) {
        message = `\`${display}\` is not part of OpenRTB ${version}. It is sent as written, and an exchange on ${version} may ignore it.`;
      } else if (has(body, fieldSegments(section, current!, every))) {
        message = `\`${display}\` is also set, but OpenRTB ${version} reads \`${currentDisplay}\`. Both are sent as written.`;
      } else {
        message = `\`${display}\` is not where OpenRTB ${version} puts this; it reads \`${currentDisplay}\`. The value is sent as written, so the exchange may not see it.`;
        movable = section !== 'imp';
      }
      issues.push({
        kind: 'wrong-place',
        message,
        path: display,
        ...(movable ? { fix: [{ section, relPath: current!, movedFrom: f.name }] } : {}),
      });
    }
  }

  // ── Value sweep — a literal that is not what the field accepts ────────────
  for (const [section, fields] of Object.entries(ORTB_FIELD_CATALOG)) {
    // Every scope an impression's value is authored in — the default and each override (RS4c).
    const scopes: Array<ImpScope | undefined> = section === 'imp' ? impScopes(body) : [undefined];
    for (const f of fields) {
      for (const scope of scopes) {
        const finding = gradeLiteral(f, read(body, fieldSegments(section, f.name, scope)));
        if (finding) {
          // **The finding's own level, never a level this surface picks** (`D41`). A constraint says
          // the value is wrong and a sentinel says it is valid and probably unintended; deciding that
          // here would be a second judgement about one value, in each of the surfaces that ask.
          issues.push({ kind: finding.kind, message: finding.message, path: displayPath(section, f.name, scope) });
        }
      }
    }
  }

  // ── A floor the source's terms will raise (#918) ──────────────────────────
  // Every scope an impression's floor is authored in; the driver bounds each after the merge, in
  // either mode. The same sentence a `bidfloor` parameter row gets.
  for (const scope of impScopes(body)) {
    const authored = authoredFloorLiteral(read(body, [...scope.segments, 'bidfloor']));
    const message = authored === null ? null : describeFloorBelowBound(`${scope.display}.bidfloor`, authored, floor);
    if (message) issues.push({ kind: 'overridden', message, path: `${scope.display}.bidfloor` });
  }

  /**
   * ── Required, and not guaranteed to arrive ─────────────────────────────────
   *
   * `X46`, requested by Todd 2026-09-02. **The same sentence the parameter rows use**
   * ({@link describeRequiredRisk}), because the two surfaces make the same decision and a
   * duplicated explanation produces a different sentence that nothing checks — read at the moment
   * an operator is most likely to believe it.
   *
   * Walks the **leaves**, not the catalogue, because the claim is the leaf's own: `$importance` is
   * what an operator or the catalogue seed stated about *this* body, and the coverage sweep above
   * asks a different question — *is this field present at all*.
   *
   * The consequence is always `omit` here: an ORTB body key that resolves to nothing is left off
   * either way (#880), which is exactly why the omission checkbox is refused on this surface.
   */
  for (const leaf of bodyToLeaves(body)) {
    const risk = describeRequiredRisk(
      {
        required: isRequiredHere(leaf.importance),
        suppressed: leaf.suppress === true,
        derived: leaf.kind === 'expression',
        hasDefault: (leafDefault(leaf, expressionLookup) ?? '').trim() !== '',
        whenUnresolved: 'omit',
      },
      `\`${leafDisplayPath(leaf)}\``,
    );
    if (risk) issues.push({ kind: 'at-risk', message: risk, path: leafDisplayPath(leaf) });

    /**
     * **A literal on a field our catalog does not know is graded by its own Type** (`FD3a`, `1f-iii`).
     * The value sweep above walks the catalog, so a vendor's `ext` field was never graded — and the
     * driver converts it by `$valueType` and leaves it out when that fails, which said nothing. A vendor
     * template declares that type on the leaf, so the leaf is the declaration it has.
     */
    if (leaf.kind === 'static' && leaf.asType && !catalogFieldForPath(leaf.path)) {
      const finding = gradeLiteral(
        { name: leafDisplayPath(leaf) },
        { $static: leaf.staticValue, $valueType: leaf.asType },
      );
      if (finding) issues.push({ kind: finding.kind, message: finding.message, path: leafDisplayPath(leaf) });
    }

    // Metadata about the demand source, going on the wire (`#1322`). A body leaf holds its path in
    // its expression; a macro leaf cannot name one, because no catalog macro resolves to an artifact.
    const display = leafDisplayPath(leaf);
    for (const message of describeArtifactMetadataOnWire(leaf, `\`${display}\``)) {
      issues.push({ kind: 'metadata-on-wire', message, path: display });
    }

    /**
     * What is unfinished **inside** the leaf's expression (`4c`, `#1334`) — and, since `4d`
     * (`#1413`), its **root** path as well.
     *
     * **A mistyped path on a body leaf was silent**, while the identical mistake on a query
     * parameter or a header has warned since `#998`. Those two raise it off `row.mappingSource`,
     * which is where a row keeps its root; a body leaf keeps its root **inside** the expression,
     * and the walk skipped the root deliberately as *the row's own validator's* — on a surface
     * whose row has none. *A rule attached to one path lapses when another is added*, with two
     * planes holding it and the third never having had it. `'walk'` is this surface's answer to
     * who reports it, and it is a declaration rather than an assumption (`RootPathOwner`).
     */
    if (leaf.kind === 'expression') {
      for (const issue of expressionStructureIssues(leaf.expression, `\`${display}\``, display, 'walk')) {
        issues.push({ kind: issue.kind, message: issue.message, path: display, at: issue.at });
      }
    }

    /**
     * **A reference is checked against what it yields** (`FD11`). The slot already declares which
     * concept it carries on 34 of this catalog's fields, and a path or a macro names one — so
     * `regs.ext.us_privacy` mapped to the viewer's IP address is checkable with nothing new
     * declared, and reported nothing until now. A `warn`, and generous: silence unless both ends
     * are declared and disagree.
     */
    const reference = checkDestinationReference(catalogFieldForPath(leaf.path), {
      macro: leaf.expression?.type === 'macro' ? leaf.expression.name : null,
      path: leaf.kind === 'expression' ? rootCalculatedPath(leaf.expression) : null,
    });
    if (reference) issues.push({ kind: reference.kind, message: reference.message, path: display });

    /**
     * **A default is a literal, and is graded like one** (`FD4`, `4b`). The value sweep above reads
     * what sits at a field's path, and a mapped leaf is a node the sweep exempts — so a default never
     * reached it, on the one surface whose defaults travel inside the document. It is the value sent
     * when the mapping resolves to nothing, which is when nobody is watching (`#914`). Read as `json`
     * unless the driver coerces it — the leaf's `Type`, else the slot's own (`FD13`, `encodingAfterCoercion`).
     */
    const defaultFinding = checkDestinationValue(
      catalogFieldForPath(leaf.path),
      leafDefault(leaf, expressionLookup),
      encodingAfterCoercion(
        'json',
        runtimeCoercion(
          leaf.asType,
          leaf.kind === 'expression' ? leaf.expression : undefined,
          catalogFieldForPath(leaf.path)?.valueType ?? null,
        ),
      ),
    );
    if (defaultFinding) issues.push({ kind: defaultFinding.kind, message: defaultFinding.message, path: display });

    /**
     * **And shape against shape** (`#1413` piece 3, `FD11` §7c, `4e`). The row's `Type`, else what
     * the expression declares, else what its shape makes certain — against what the slot declares.
     * A list into `site.page` is reported, and the redirect offers one element of it where the value
     * is a path, and *Send structured values as* where the row offers it (`4f`, `#1416`) — read from
     * `bodyLeafRendering`, the rule the control itself is offered by, so the sentence can never name a
     * control the row does not show (`#1272`). A row that has authored a rendering has answered.
     */
    if (leaf.kind === 'expression' && leaf.expression) {
      const lookup = expressionLookup ? (ref: string) => expressionLookup(ref)?.expression : null;
      const target = leaf.expression.type === 'reference' ? expressionLookup?.(leaf.expression.ref) : undefined;
      const shape = checkYieldAgainstDestination(
        catalogFieldForPath(leaf.path),
        rowYield(leaf.asType, leaf.expression, lookup),
        {
          from: target?.name ?? null,
          path: rootCalculatedPath(leaf.expression),
          offersRendering: bodyLeafRendering(leaf, lookup).offered,
          rendered: leaf.sendAs !== undefined,
          textSurface: false,
        },
      );
      if (shape) issues.push({ kind: shape.kind, message: shape.message, path: display });
    }
  }

  return issues;
}

/**
 * **A literal is graded as the driver will send it** (`FD13`, Todd 2026-09-25).
 *
 * The driver converts a literal to its row's Type, else to the field's type (`ortbFieldSentType`),
 * through `convertScalar`. So this converts it the same way and grades the result: `"1009"` on
 * `device.w` is `1009` and fine; `acme` there cannot convert, so the driver leaves it out, which is the
 * finding; and a row Type the field disagrees with, such as *string* on
 * `device.w`, sends `"1009"`, which is graded as the text it becomes.
 *
 * **It reaches a literal inside a node**, which it did not before: `{"$static": "acme", "$valueType":
 * "number"}` was exempt as *not a literal yet*, so a literal that carried a Type or a note was never
 * checked. A mapped value (`$expr`, `$macro`) is still exempt, since what it resolves to is unknown.
 */
/** What grading reads of a destination: a catalog field, or a leaf that declares its own type. */
type GradedDestination = Pick<DestinationField, 'name' | 'valueType' | 'value' | 'constraint' | 'readAs'>;

function gradeLiteral(field: GradedDestination, raw: unknown) {
  const node =
    isOrtbBodyLeafNode(raw) && !('$expr' in raw) && !('$macro' in raw) && '$static' in raw
      ? (raw as OrtbBodyLeafNode & { $static: unknown })
      : undefined;
  const literal = node ? node.$static : raw;
  const declared = node ? ortbBodyLeafValueType(node) : undefined;
  const rowType = isExpressionYieldType(declared) ? declared : undefined;
  const asType = coercibleScalar(rowType ?? ortbFieldSentType(field) ?? undefined);
  const scalar = typeof literal === 'string' || typeof literal === 'number' || typeof literal === 'boolean';
  if (!asType || !scalar || (typeof literal === 'string' && isDeferredText(literal))) {
    return checkDestinationValue(field, literal, 'json');
  }
  const sent = convertScalar(String(literal), asType);
  if (sent !== undefined) return checkDestinationValue(field, sent, 'json');
  // It cannot be converted, so the driver leaves it out, and sends its own value where it builds one.
  const wanted = DESTINATION_TYPE_PHRASE[asType];
  return {
    kind: 'wrong-type' as const,
    message: rowType
      ? `This row's Type is ${wanted} and "${String(literal)}" is not one, so "${field.name}" is left out of the request.`
      : `"${field.name}" expects ${wanted} and "${String(literal)}" is not one, so it is left out of the request.`,
  };
}

/**
 * **A leaf's address, spelled the way the row that holds it is** — {@link pathToLabel}, not a
 * private reduce (found building `4d`, `#1413`).
 *
 * It had its own copy, written before the `imp` patterns arrived (RS4c), so a leaf under `$each`
 * came out as `imp.$each.video.w` while every other finding in this file — and the row itself —
 * says `imp[*].video.w`. Two things followed, and the second is the expensive one: the sentence
 * named **our storage spelling** rather than the address the operator authored (`#1045`), and
 * `OrtbBodyEditor` uses this string as the finding's `ref`, so the row mark and the Fix button had
 * nothing to match on for every `$each` leaf. *Which row is this finding about* is one question
 * (`#1412`), and this was a second answer to it.
 */
function leafDisplayPath(leaf: { path: PathSegment[] }): string {
  return pathToLabel(leaf.path);
}
